import type { FC } from 'react';

import { Placeholder, useRendering } from '@eneco/cms';
import { Stack } from '@eneco/sparky';

import { MEGA_MENU_SLOTS, resolveSlot } from './slots';

/**
 * `MegaMenu` block for the CMS-agnostic lane, serving **both** providers. A container for its
 * divisions, with an optional promo-card column beside them.
 *
 * Known simplification: the legacy component builds a Radix `NavigationMenu` out of
 * `@eneco/custom-components/MegaMenu` — hover-opened dropdowns on desktop, a dialog on mobile. That
 * lib is outside `lib:cms-components`'s module boundary (`nx.json`), so this renders the same
 * divisions as a plain list. Same call `MainNavigation` makes about the production header: this lane
 * compares content pipelines, not interactive chrome.
 *
 * Carries no content of its own worth rendering — its `title` is never shown by the legacy component
 * either, and its `siteWideContent` reference resolves to the shared navigation document its
 * divisions come from.
 */
const MegaMenu: FC = () => {
  const rendering = useRendering();

  const divisionsSlot = resolveSlot(rendering, MEGA_MENU_SLOTS.divisions);
  const cardsSlot = resolveSlot(rendering, MEGA_MENU_SLOTS.cards);

  if (!divisionsSlot && !cardsSlot) return null;

  return (
    <Stack direction={{ initial: 'column', lg: 'row' }} gap="7" as="nav">
      {divisionsSlot && (
        <Stack.Item grow>
          <Stack direction={{ initial: 'column', lg: 'row' }} gap="6">
            <Placeholder name={divisionsSlot} />
          </Stack>
        </Stack.Item>
      )}
      {cardsSlot && (
        <Stack.Item grow>
          <Placeholder name={cardsSlot} />
        </Stack.Item>
      )}
    </Stack>
  );
};

export default MegaMenu;
